import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackAuthenticatedVisit } from '../services/visitService';

/**
 * Component to track page visits
 * This component should be placed inside the Router
 */
const VisitTracker = () => {
  const location = useLocation();
  const { firebaseUser, isLoading } = useAuth();
  const lastTrackedPath = useRef(null);
  const trackingTimeout = useRef(null);

  useEffect(() => {
    // Don't track until auth has finished loading
    if (isLoading) {
      console.log('⏳ Waiting for auth to load before tracking...');
      return;
    }

    const currentPath = location.pathname;
    
    // Only track if this is a different path - CHECK BEFORE SETTING TIMEOUT
    if (lastTrackedPath.current === currentPath) {
      console.log('⏭️ Skipping duplicate tracking for:', currentPath);
      return;
    }

    // Debounce tracking to prevent duplicates from React StrictMode
    const trackPageVisit = async () => {
      // Clear any pending tracking
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }
      
      // Small debounce to prevent React StrictMode duplicates
      trackingTimeout.current = setTimeout(async () => {
        try {
          console.log('🔍 Tracking visit:', {
            path: currentPath,
            hasUser: !!firebaseUser,
            userId: firebaseUser?.uid || 'none',
            userEmail: firebaseUser?.email || 'none'
          });
          await trackAuthenticatedVisit(currentPath, firebaseUser);
          lastTrackedPath.current = currentPath;
        } catch (error) {
          // Fail silently for analytics - don't disrupt user experience
          console.debug('Visit tracking failed:', error);
        }
      }, 100);
    };

    trackPageVisit();
    
    // Cleanup timeout on unmount
    return () => {
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }
    };
  }, [location.pathname, firebaseUser, isLoading]);

  // This component doesn't render anything
  return null;
};

export default VisitTracker;